'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
    const floatRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!floatRef.current) return;
            const { innerWidth, innerHeight } = window;
            const x = (e.clientX / innerWidth - 0.5) * 20;
            const y = (e.clientY / innerHeight - 0.5) * 14;
            floatRef.current.style.transform = `translate(${x}px, ${y}px)`;
        };
        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    return (
        <section className="relative min-h-screen flex items-center pt-28 pb-16 px-6 md:px-10 overflow-hidden">
            {/* Atmospheric depth layers */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] blob-primary opacity-60" />
                <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] blob-accent opacity-50" />
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0,212,170,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(124,58,237,0.06) 0%, transparent 50%)' }} />

            </div>

            <div className="max-w-7xl mx-auto w-full relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

                    {/* Left: Copy */}
                    <div className="lg:col-span-5 flex flex-col gap-8">
                        {/* Status badge */}
                        <div className="inline-flex items-center gap-2 self-start glass-card px-3.5 py-2 rounded-full">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                            </span>
                            <span className="section-label text-primary">
                                Building the future — {new Date()?.getFullYear()}
                            </span>
                        </div>

                        {/* Headline */}
                        <h1 className="text-hero-xl text-foreground">
                            Building{' '}
                            <span className="gradient-text">technology</span>
                            <br />
                            that moves
                            <br />
                            people forward.
                        </h1>

                        {/* Subtext */}
                        <p className="text-lg font-400 leading-relaxed text-muted-foreground max-w-lg">
                            Codigo builds intelligent digital products and technology systems designed to solve real problems, simplify complexity, and create better ways to learn, work, and build.
                        </p>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row gap-3 mt-2">
                            <Link href="/products" className="btn-primary">
                                Explore our products
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                            <Link href="/contact" className="btn-secondary">
                                Work with Codigo
                            </Link>
                        </div>

                        {/* Trust signal */}
                        <div className="flex items-center gap-4 pt-4 border-t border-border">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-primary" />
                                <span className="text-xs text-muted-foreground font-500">2 flagship products</span>
                            </div>
                            <div className="w-px h-4 bg-border" />
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-accent" />
                                <span className="text-xs text-muted-foreground font-500">Global reach</span>
                            </div>
                            <div className="w-px h-4 bg-border" />
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
                                <span className="text-xs text-muted-foreground font-500">Nigerian-built</span>
                            </div>
                        </div>
                    </div>

                    {/* Right: Asymmetric visual grid */}
                    <div className="lg:col-span-7 relative h-[560px] hidden lg:block" ref={floatRef}
                        style={{ transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)' }}>
                        <div className="grid grid-cols-12 grid-rows-6 gap-3 h-full w-full">

                            {/* Tall left card — Wisly */}
                            <div className="col-span-5 row-span-6 rounded-2xl overflow-hidden relative group cursor-pointer gradient-border"
                                style={{ background: 'linear-gradient(145deg, #131520 0%, #0d1117 100%)' }}>
                                <div className="absolute inset-0 opacity-30">
                                    <AppImage
                                        src="https://img.rocket.new/generatedImages/rocket_gen_img_1dce37fbd-1776193557362.png"
                                        alt="Students in bright classroom collaborating on laptops, warm natural light, modern educational environment"
                                        fill
                                        className="object-cover group-hover:scale-105 transition-transform duration-700" />

                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                                <div className="absolute bottom-6 left-6 right-6">
                                    <span className="section-label text-primary mb-2 block">Education</span>
                                    <h3 className="text-xl font-700 text-foreground tracking-tight">Wisly</h3>
                                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                        Connected ecosystem for students, parents & institutions
                                    </p>
                                </div>
                                {/* Top badge */}
                                <div className="absolute top-4 left-4 glass-card px-2.5 py-1.5 rounded-full flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                    <span className="text-xs font-600 text-primary">EdTech</span>
                                </div>
                            </div>

                            {/* Top right — CapState */}
                            <div className="col-span-7 row-span-3 rounded-2xl overflow-hidden relative group cursor-pointer"
                                style={{ background: 'linear-gradient(145deg, #0f0a1a 0%, #131520 100%)' }}>
                                <div className="absolute inset-0 opacity-25">
                                    <AppImage
                                        src="https://img.rocket.new/generatedImages/rocket_gen_img_178ed799d-1785088751486.png"
                                        alt="Code editor on dark screen showing clean programming interface, deep shadows, dark steel blue background, low-key developer lighting"
                                        fill
                                        className="object-cover group-hover:scale-105 transition-transform duration-700" />

                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                                <div className="absolute bottom-5 left-5 right-5">
                                    <span className="section-label text-accent mb-1.5 block">Developer Tools</span>
                                    <h3 className="text-lg font-700 text-foreground tracking-tight">CapState</h3>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Restore the exact code you need — not entire files
                                    </p>
                                </div>
                                <div className="absolute top-4 right-4 glass-card px-2.5 py-1.5 rounded-full flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                                    <span className="text-xs font-600 text-accent">Version Control</span>
                                </div>
                            </div>

                            {/* Bottom right row — Stat + Spinning ring */}
                            <div className="col-span-4 row-span-3 rounded-2xl glass-card p-5 flex flex-col justify-between">
                                <div>
                                    <p className="section-label text-muted-foreground mb-3">Products live</p>
                                    <p className="stat-number gradient-text">2+</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-primary" />
                                        <span className="text-xs text-muted-foreground">Wisly — EdTech</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-accent" />
                                        <span className="text-xs text-muted-foreground">CapState — DevTools</span>
                                    </div>
                                </div>
                            </div>

                            {/* Spinning text ring */}
                            <div className="col-span-3 row-span-3 flex items-center justify-center relative">
                                <div className="relative w-28 h-28 flex items-center justify-center">
                                    <svg className="w-full h-full absolute animate-spin-slow text-muted-foreground" viewBox="0 0 100 100">
                                        <path id="hero-curve" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" fill="transparent" />
                                        <text className="text-[8px] uppercase font-700 tracking-widest fill-current">
                                            <textPath href="#hero-curve">
                                                • Codigo • Intelligent • Systems •
                                            </textPath>
                                        </text>
                                    </svg>
                                    <Link
                                        href="/products"
                                        className="w-14 h-14 rounded-full bg-primary flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg z-10"
                                        style={{ boxShadow: '0 0 30px rgba(0,212,170,0.4)' }}>

                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M5 15L15 5M15 5H7M15 5v8" stroke="var(--primary-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Floating card */}
                        <div className="absolute top-1/3 right-[-24px] glass-card p-5 rounded-2xl w-56 animate-float"
                            style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
                            <div className="flex items-center justify-between mb-3">
                                <span className="section-label text-muted-foreground">Latest Build</span>
                                <span className="w-2 h-2 rounded-full bg-primary" />
                            </div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-accent/20 text-accent">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M2 4l4-2 4 2 4-2v10l-4 2-4-2-4 2V4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-600 text-foreground">CapState v2.0</p>
                                    <p className="text-xs text-muted-foreground">Granular restore</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs font-600 text-primary bg-primary/10 px-2.5 py-1.5 rounded-lg w-fit">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M10 3L5 9 2 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Shipped
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}